import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { EnvironmentService, EventService, ListService, fromList } from '@core/services';
import { GridService } from '@core/services';
import { currentTimeInSeconds } from '@util/list';

import { ListOfValuesResponse, MultipleListOfValuesResponse } from '#types/list';

import { AuthActions } from '@auth/state';
import { CONSTANTS } from '@facility/constants';
import { FacilityService } from '@facility/services';

import * as FacilityActions from './facility.actions';

@Injectable()
export class FacilityEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private gridService: GridService,
    private listService: ListService,
    private environmentService: EnvironmentService,
    private facilityService: FacilityService,
    private eventService: EventService
  ) {}

  RequestLists$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FacilityActions.RequestLists),
        concatLatestFrom(() => [this.store.select(fromList.selectFacilityLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(FacilityActions.RequestFacilityLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  requestFacilityList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FacilityActions.RequestFacilityLists, AuthActions.LoginSuccess),
      exhaustMap(() => {
        return this.listService.getListOfValues<MultipleListOfValuesResponse>('facility').pipe(
          this.eventService.indicate(CONSTANTS.LIST_ALL),
          map((lists) => FacilityActions.RequestFacilityListsSuccess({ lists })),
          catchError((error) => of(FacilityActions.RequestFacilityListsFailure(error)))
        );
      })
    );
  });

  requestFacilityDictionaryFieldList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FacilityActions.RequestFacilityDictionaryFieldList),
      exhaustMap((parameters) => {
        return this.listService
          .getListOfValuesWithParameters<ListOfValuesResponse>('facility', 'facilityDict', { facility: parameters.facility })
          .pipe(
            this.eventService.indicate(CONSTANTS.LIST_FACILITY_DICT),
            map((list) => FacilityActions.RequestFacilityDictionaryFieldListSuccess({ facility: parameters.facility, list })),
            catchError((error) => of(FacilityActions.RequestFacilityDictionaryFieldListFailure(error)))
          );
      })
    );
  });

  FacilityLookupRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        FacilityActions.FacilityLookupDialogRequestData,
        FacilityActions.FacilityLookupDialogRequestDataWithFilters,
        FacilityActions.FacilityLookupDialogRequestDataWithParameters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.facilityService.getFacilityLookup({ filters, page, rows, sorts }).pipe(
          map((data) => FacilityActions.FacilityLookupDialogRequestDataSuccess({ id, data })),
          catchError((_error) => of(FacilityActions.FacilityLookupDialogRequestDataFailure({ id })))
        )
      )
    );
  });
}
